import React, { useState } from 'react'
import { Bold, Italic, Underline, Type, Palette } from 'lucide-react'

const PartialTextStylingDemo = () => {
  const [richText, setRichText] = useState({
    content: 'Ramesh Kumar Singh',
    spans: [
      { start: 0, end: 6, styles: { fontWeight: 'bold', textDecoration: 'underline', fontSize: '24px', color: '#ff0000' } },
      { start: 7, end: 12, styles: { fontStyle: 'italic', fontSize: '18px', color: '#00ff00' } },
      { start: 13, end: 18, styles: { fontFamily: 'Georgia', fontSize: '20px', color: '#0000ff' } }
    ]
  })

  const renderRichText = () => {
    const { content, spans } = richText
    if (!spans || !spans.length) return content

    const sorted = [...spans].sort((a, b) => a.start - b.start)
    let html = ''
    let cursor = 0

    sorted.forEach(({ start, end, styles }) => {
      // Add unstyled text before this span
      if (start > cursor) {
        html += content.slice(cursor, start)
      }

      // Add styled span
      const text = content.slice(start, end)
      const styleStr = Object.entries(styles || {})
        .map(([k, v]) => `${k.replace(/[A-Z]/g, m => '-' + m.toLowerCase())}:${v}`)
        .join(';')
      html += `<span style="${styleStr}">${text}</span>`
      cursor = end
    })

    // Add remaining unstyled text
    if (cursor < content.length) {
      html += content.slice(cursor)
    }

    return html
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">Partial Text Styling Demo</h2>
      
      {/* Demo Text Display */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-4">Styled Text Result:</h3>
        <div 
          className="p-4 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 text-center"
          dangerouslySetInnerHTML={{ __html: renderRichText() }}
        />
      </div>

      {/* JSON Structure Display */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-4">Rich Text JSON Structure:</h3>
        <pre className="bg-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
          {JSON.stringify(richText, null, 2)}
        </pre>
      </div>

      {/* How It Works */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-4">How It Works:</h3>
        <div className="space-y-4 text-sm">
          <div className="p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold text-blue-800 mb-2">1. Text Content</h4>
            <p className="text-blue-700">The base text: "{richText.content}"</p>
          </div>
          
          <div className="p-4 bg-green-50 rounded-lg">
            <h4 className="font-semibold text-green-800 mb-2">2. Style Spans</h4>
            <ul className="text-green-700 space-y-1">
              <li><strong>Characters 0-6 ("Ramesh"):</strong> Bold, Underlined, 24px, Red</li>
              <li><strong>Characters 7-12 ("Kumar"):</strong> Italic, 18px, Green</li>
              <li><strong>Characters 13-18 ("Singh"):</strong> Georgia font, 20px, Blue</li>
            </ul>
          </div>

          <div className="p-4 bg-purple-50 rounded-lg">
            <h4 className="font-semibold text-purple-800 mb-2">3. Rendering Process</h4>
            <p className="text-purple-700">
              The system processes each span in order, applying styles to the specified character ranges.
              Overlapping spans are supported, and styles can be combined.
            </p>
          </div>
        </div>
      </div>

      {/* Usage Instructions */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-4">How to Use in Editor:</h3>
        <div className="space-y-3 text-sm">
          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">1</div>
            <p>Double-click any text element to enter edit mode</p>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">2</div>
            <p>Select the text you want to style (e.g., select "Ramesh")</p>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">3</div>
            <p>Use the formatting buttons in the properties panel: <Bold className="w-4 h-4 inline mx-1" /><Italic className="w-4 h-4 inline mx-1" /><Underline className="w-4 h-4 inline mx-1" /></p>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">4</div>
            <p>Change font size, color, or font family while text is selected</p>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">5</div>
            <p>Click outside the text or press Ctrl+Enter to save changes</p>
          </div>
        </div>
      </div>

      {/* Features */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Features:</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="p-3 bg-gray-50 rounded-lg">
            <h4 className="font-semibold mb-2">✨ Partial Styling</h4>
            <p>Style individual words or characters within a text element</p>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg">
            <h4 className="font-semibold mb-2">🎨 Multiple Styles</h4>
            <p>Bold, italic, underline, colors, fonts, sizes</p>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg">
            <h4 className="font-semibold mb-2">📄 PDF Export</h4>
            <p>Rich text renders correctly in PDF exports</p>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg">
            <h4 className="font-semibold mb-2">🔄 Overlapping Spans</h4>
            <p>Multiple styles can be applied to the same text</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PartialTextStylingDemo
